import { createAction, createReducer } from '@reduxjs/toolkit';
import { auth } from 'services/firebase';
import { Admin, AuthState } from 'shared/types';

// Load authentication state from localStorage
const storedAuth = JSON.parse(localStorage.getItem('authState') || '{}');

const initialState: AuthState = {
  isAuth: false,
  isInitializing: true,
  user: storedAuth?.user || null,
  impersonation: {
    isImpersonating: Boolean(storedAuth?.impersonation?.impersonatedBy),
    impersonatedBy: storedAuth?.impersonation?.impersonatedBy || null,
    impersonatedAt: storedAuth?.impersonation?.impersonatedAt || null,
    originalSuperAdminUid: storedAuth?.impersonation?.originalSuperAdminUid || null,
  },
};

// Actions
export const loginUserSuccess = createAction<Admin | null>('auth/loginUserSuccess');
export const loginUserFail = createAction('auth/loginUserFail');
export const logoutUser = createAction('auth/logout');
export const updateUser = createAction<Admin>('auth/updateUser');
export const refreshUserFromStorage = createAction('auth/refreshUserFromStorage');
export const setInitialized = createAction<boolean>('auth/setInitialized');
export const setImpersonationState = createAction<{
  impersonatedBy: string | null;
  impersonatedAt: number | null;
  originalSuperAdminUid?: string | null;
}>('auth/setImpersonationState');
export const clearImpersonationState = createAction('auth/clearImpersonationState');

// Reducer
export const authReducer = createReducer(initialState, (builder): void => {
  builder
    .addCase(setInitialized, (state, action): void => {
      state.isInitializing = false;
      state.isAuth = action.payload;
    })
    .addCase(loginUserSuccess, (state, action): void => {
      state.isAuth = true;
      state.isInitializing = false;
      const user = action.payload;

      if (user) {

        if (!('role' in user) || !user.role) {
          (user as any).role = user.role;
        }
      }

      state.user = user;
      localStorage.setItem('authState', JSON.stringify(state));
    })
    .addCase(loginUserFail, (state): void => {
      state.isAuth = false;
      state.isInitializing = false;
      state.user = null;
      state.impersonation = {
        isImpersonating: false,
        impersonatedBy: null,
        impersonatedAt: null,
        originalSuperAdminUid: null,
      };
    })
    .addCase(logoutUser, (state): void => {
      localStorage.removeItem('authState');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      auth.signOut();
      state.isAuth = false;
      state.user = null;
      state.impersonation = {
        isImpersonating: false,
        impersonatedBy: null,
        impersonatedAt: null,
        originalSuperAdminUid: null,
      };
    })
    .addCase(updateUser, (state, action): void => {
      const user = action.payload;
      if (user) {

        if (!('role' in user) || !user.role) {
          (user as any).role = user.role;
        }
      }
      state.user = user;
      localStorage.setItem('authState', JSON.stringify(state));
    })
    .addCase(refreshUserFromStorage, (state): void => {
      const stored = JSON.parse(localStorage.getItem('authState') || '{}');
      state.isAuth = Boolean(stored?.isAuth && auth.currentUser);
      state.user = stored?.user || null;
      state.impersonation = {
        isImpersonating: Boolean(stored?.impersonation?.impersonatedBy),
        impersonatedBy: stored?.impersonation?.impersonatedBy || null,
        impersonatedAt: stored?.impersonation?.impersonatedAt || null,
        originalSuperAdminUid: stored?.impersonation?.originalSuperAdminUid || null,
      };
    })
    .addCase(setImpersonationState, (state, action): void => {
      state.impersonation = {
        isImpersonating: Boolean(action.payload.impersonatedBy),
        impersonatedBy: action.payload.impersonatedBy,
        impersonatedAt: action.payload.impersonatedAt,
        originalSuperAdminUid:
          action.payload.originalSuperAdminUid ?? state.impersonation.originalSuperAdminUid ?? null,
      };
      localStorage.setItem('authState', JSON.stringify(state));
    })
    .addCase(clearImpersonationState, (state): void => {
      state.impersonation = {
        isImpersonating: false,
        impersonatedBy: null,
        impersonatedAt: null,
        originalSuperAdminUid: null,
      };
      localStorage.setItem('authState', JSON.stringify(state));
    });
});

